import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Download, ShieldAlert, ShieldCheck, ImageIcon, Loader2, Copy, Lock, Award, BadgeCheck, Bookmark } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const LANGUAGES = [
  { id: "english", label: "English", free: true },
  { id: "hindi", label: "Hindi" },
  { id: "bengali", label: "Bengali" },
  { id: "tamil", label: "Tamil" },
  { id: "marathi", label: "Marathi" },
];

const BACKLINK_PAGES = [
  { label: "Home", url: "https://tejomdigital.com" },
  { label: "Blog", url: "https://tejomdigital.com/blog" },
  { label: "Services", url: "https://tejomdigital.com/services" },
  { label: "Contact", url: "https://tejomdigital.com/contact" },
];

function download(filename, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

async function pollJob(url, { interval = 3000, timeout = 300000 } = {}) {
  const deadline = Date.now() + timeout;
  while (Date.now() < deadline) {
    const { data } = await api.get(url);
    if (data.status === "done" || data.status === "error") return data;
    await new Promise((r) => setTimeout(r, interval));
  }
  throw new Error("This is taking longer than expected. Please try again.");
}

const SEO_FIELDS = [
  ["keyword", "Keyword"], ["title", "Title"], ["slug", "Slug (English)"],
  ["meta_description", "Meta Description (≤90)"], ["social_description", "Social Description (≤90)"],
  ["meta_tags", "Meta Tags"], ["excerpt", "Excerpt (≤120)"],
];

export default function AiWorkspace({ article, premium, onUpgrade, onUsageChange, onRewritten }) {
  const [language, setLanguage] = useState("english");
  const [keyword, setKeyword] = useState("");
  const [wantSeo, setWantSeo] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copyright, setCopyright] = useState(null);
  const [image, setImage] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);
  const [tab, setTab] = useState("article");
  const [threshold, setThreshold] = useState(90);
  const [wordLimit, setWordLimit] = useState(800);
  const [backlinkUrl, setBacklinkUrl] = useState(BACKLINK_PAGES[0].url);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const authScore = result?.authenticity?.score ?? null;
  const authFlagged = authScore !== null && authScore < threshold;
  const eeat = result?.eeat;

  const gatedLang = (id) => !premium && id !== "english";

  const generate = async () => {
    if (!article) return;
    setGenerating(true);
    setResult(null); setCopyright(null); setImage(null); setSaved(false);
    try {
      const { data: start } = await api.post("/ai/rewrite", {
        title: article.title,
        content: article.summary || article.title,
        language, want_seo: wantSeo, keyword, word_limit: wordLimit,
        link: article.link, source: article.source, category: article.category,
        backlink_url: backlinkUrl,
      });
      const data = await pollJob(`/ai/rewrite/${start.job_id}`);
      if (data.status === "error") throw new Error(data.error || "Generation failed");
      setResult(data.result);
      setCopyright(data.copyright);
      setTab("article");
      onUsageChange?.();
      onRewritten?.();
      const score = data.result?.authenticity?.score;
      if (data.copyright?.flagged) {
        toast.error("Copyright risk flagged", { description: data.copyright.reason });
      } else if (typeof score === "number" && score < threshold) {
        toast.error(`Authenticity ${score}% — below ${threshold}%`, { description: "Review flagged content before publishing." });
      } else {
        toast.success("Article generated", { description: `${data.result.word_count} words · ${score}% authentic` });
      }
    } catch (e) {
      const raw = e?.response ? apiErr(e) : (e?.message || "Generation failed");
      const low = raw.toLowerCase();
      let friendly = raw;
      if (low.includes("quota") || low.includes("exceeded") || low.includes("429")) {
        friendly = "Your Google Gemini quota is exhausted. Wait a bit and retry, or enable billing on your Google AI Studio key.";
      } else if (low.includes("503") || low.includes("unavailable") || low.includes("high demand") || low.includes("overloaded")) {
        friendly = "Gemini is busy right now. Please try again in a moment.";
      }
      toast.error("Generation failed", { description: friendly });
      if (e?.response?.status === 403) onUpgrade?.();
    } finally {
      setGenerating(false);
    }
  };

  const saveArticle = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await api.post("/saved", {
        title: result.seo?.title || article.title,
        language,
        article: result.article,
        word_count: result.word_count,
        seo: result.seo || null,
        authenticity: result.authenticity || null,
        eeat: result.eeat || null,
        copyright: copyright || null,
        image_data_url: image?.data_url || "",
        source: article.source || "",
        source_via: !!result.primary_source?.via,
        link: result.primary_source?.url || article.link || "",
        category: article.category || "",
      });
      setSaved(true);
      toast.success("Saved to Library");
    } catch (e) {
      toast.error("Save failed", { description: apiErr(e) });
    } finally {
      setSaving(false);
    }
  };

  const genImage = async () => {
    if (!premium) { onUpgrade?.(); return; }
    if (!article) return;
    setImgLoading(true);
    try {
      const { data: start } = await api.post("/ai/image", { topic: article.title });
      const data = await pollJob(`/ai/image/${start.job_id}`);
      if (data.status === "error") throw new Error(data.error || "Image generation failed");
      const img = data.result;
      setImage(img);
      setTab("image");
      toast.success("Image ready", { description: `${img.size_kb} KB · ${img.width}×${img.height}` });
    } catch (e) {
      const msg = e?.response ? apiErr(e) : (e?.message || "Image generation failed");
      toast.error("Image failed", { description: msg });
      if (e?.response?.status === 403) onUpgrade?.();
    } finally {
      setImgLoading(false);
    }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  if (!article) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8 text-muted-foreground">
        <Sparkles className="w-8 h-8 mb-4 text-[#111110]" aria-hidden="true" />
        <p className="font-heading font-bold text-lg text-foreground mb-1">AI Workspace</p>
        <p className="text-sm max-w-xs">Select a news story from the feed to rewrite it, generate SEO metadata and a minimalist image.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-5 border-b border-border bg-white">
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">Selected story</p>
        <h3 className="font-heading font-bold text-sm leading-snug line-clamp-2">{article.title}</h3>
      </div>

      {/* Controls */}
      <div className="p-5 border-b border-border space-y-4 bg-white">
        <div>
          <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Language</Label>
          <div className="grid grid-cols-3 gap-1 mt-2">
            {LANGUAGES.map((l) => (
              <button
                key={l.id}
                data-testid={`lang-${l.id}`}
                onClick={() => gatedLang(l.id) ? onUpgrade?.() : setLanguage(l.id)}
                className={`relative py-2 text-xs font-mono border transition-colors ${
                  language === l.id ? "bg-[#ffce00] text-black border-[#111110]" : "bg-white border-border hover:border-foreground"
                } ${gatedLang(l.id) ? "opacity-60" : ""}`}
              >
                {l.label}
                {gatedLang(l.id) && <Lock className="w-2.5 h-2.5 absolute top-1 right-1" aria-hidden="true" />}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label htmlFor="kw" className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Focus keyword (optional)</Label>
          <Input id="kw" data-testid="keyword-input" value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="e.g. AI policy" className="mt-1 rounded-none" />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox data-testid="seo-checkbox" checked={wantSeo} onCheckedChange={(v) => setWantSeo(!!v)} />
          <span className="text-sm">Generate Technical SEO metadata</span>
        </label>

        <div>
          <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Max length</Label>
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[500, 800, 1000, 1500].map((w) => (
              <button
                key={w}
                data-testid={`wordlimit-${w}`}
                onClick={() => setWordLimit(w)}
                className={`py-2 text-xs font-mono border transition-colors ${
                  wordLimit === w ? "bg-[#ffce00] text-black border-[#111110]" : "bg-white border-border hover:border-foreground"
                }`}
              >
                ≤{w}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Backlink page (tejomdigital.com)</Label>
          <div className="grid grid-cols-4 gap-1 mt-2">
            {BACKLINK_PAGES.map((p) => (
              <button
                key={p.url}
                data-testid={`backlink-${p.label.toLowerCase()}`}
                onClick={() => setBacklinkUrl(p.url)}
                className={`py-2 text-xs font-mono border transition-colors ${
                  backlinkUrl === p.url ? "bg-[#ffce00] text-black border-[#111110]" : "bg-white border-border hover:border-foreground"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="thr" className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            Authenticity flag threshold
          </Label>
          <div className="flex items-center gap-1">
            <Input id="thr" data-testid="threshold-input" type="number" min={0} max={100} value={threshold}
              onChange={(e) => setThreshold(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
              className="w-16 h-8 rounded-none text-right font-mono text-xs" />
            <span className="font-mono text-xs text-muted-foreground">%</span>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            data-testid="generate-btn"
            onClick={generate}
            disabled={generating}
            className="flex-1 rounded-none bg-[#111110] hover:bg-[#ffce00] text-white hover:text-black transition-colors font-mono text-xs uppercase tracking-wider"
          >
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" /> : <Sparkles className="w-4 h-4 mr-2" aria-hidden="true" />}
            Rewrite (≤{wordLimit} words)
          </Button>
          <Button
            data-testid="generate-image-btn"
            onClick={genImage}
            disabled={imgLoading}
            variant="outline"
            className="rounded-none font-mono text-xs"
            title="Generate AI image (premium)"
          >
            {imgLoading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <ImageIcon className="w-4 h-4" aria-hidden="true" />}
            {!premium && <Lock className="w-3 h-3 ml-1" aria-hidden="true" />}
          </Button>
        </div>
      </div>

      {/* Output */}
      <div className="flex-1 overflow-y-auto">
        {copyright && (
          <div
            data-testid="copyright-banner"
            className={`px-5 py-3 flex items-start gap-3 border-b border-border ${
              copyright.flagged ? "bg-[#FDECEC] text-[#B3232F]" : "bg-[#E9F6F4] text-[#1E6B60]"
            }`}
          >
            {copyright.flagged
              ? <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
              : <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />}
            <p className="text-xs leading-relaxed font-mono">{copyright.reason}</p>
          </div>
        )}

        {authScore !== null && (
          <div
            data-testid="authenticity-banner"
            className={`px-5 py-3 flex items-start gap-3 border-b border-border ${
              authFlagged ? "bg-[#FDECEC] text-[#B3232F]" : "bg-[#F4F7EE] text-[#3B5323]"
            }`}
          >
            {authFlagged
              ? <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
              : <BadgeCheck className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />}
            <div className="flex-1">
              <p className="text-xs font-mono font-semibold" data-testid="authenticity-score">
                {authFlagged ? `🚩 RED FLAG — Authenticity ${authScore}% (below ${threshold}%)` : `Authenticity ${authScore}%`}
              </p>
              <p className="text-[11px] leading-relaxed font-mono opacity-80 mt-0.5">{result?.authenticity?.notes}</p>
            </div>
          </div>
        )}

        {!result && !image && (
          <div className="p-8 text-center text-muted-foreground text-sm">
            Configure options above, then generate your rewritten article.
          </div>
        )}

        {(result || image) && (
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="w-full justify-start rounded-none border-b border-border bg-white h-auto p-0">
              <TabsTrigger data-testid="tab-article" value="article" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#ffce00] font-mono text-xs uppercase">Article</TabsTrigger>
              {result?.seo && <TabsTrigger data-testid="tab-seo" value="seo" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#ffce00] font-mono text-xs uppercase">SEO</TabsTrigger>}
              <TabsTrigger data-testid="tab-image" value="image" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#ffce00] font-mono text-xs uppercase">Image</TabsTrigger>
              {eeat && <TabsTrigger data-testid="tab-ranking" value="ranking" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#ffce00] font-mono text-xs uppercase">Ranking</TabsTrigger>}
            </TabsList>

            <TabsContent value="article" className="p-5 m-0">
              {result ? (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-mono text-[11px] text-muted-foreground">{result.word_count} words · {language}</span>
                    <div className="flex gap-2">
                      <Button data-testid="save-article" size="sm" variant="outline" className="rounded-none h-7" onClick={saveArticle} disabled={saving || saved}>
                        <Bookmark className={`w-3 h-3 mr-1 ${saved ? "fill-current" : ""}`} aria-hidden="true" />{saved ? "Saved" : "Save"}
                      </Button>
                      <Button data-testid="copy-article" size="sm" variant="ghost" className="rounded-none h-7" onClick={() => copy(result.article)}><Copy className="w-3 h-3 mr-1" aria-hidden="true" />Copy</Button>
                      <Button data-testid="download-article" size="sm" variant="outline" className="rounded-none h-7" onClick={() => download(`${result.seo?.slug || "article"}.txt`, result.article)}><Download className="w-3 h-3 mr-1" aria-hidden="true" />TXT</Button>
                    </div>
                  </div>
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{result.article}</div>
                </>
              ) : <p className="text-sm text-muted-foreground">No article yet.</p>}
            </TabsContent>

            {eeat && (
              <TabsContent value="ranking" className="p-5 m-0" data-testid="ranking-panel">
                <div className="border border-[#111110] bg-[#111110] text-white p-4 mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-[#ffce00]" aria-hidden="true" />
                    <span className="font-mono text-[11px] uppercase tracking-wider">Google E-E-A-T score</span>
                  </div>
                  <span className="font-heading font-extrabold text-2xl text-[#ffce00]" data-testid="eeat-overall">{eeat.overall_pct}%</span>
                </div>
                {[
                  ["Expertise", eeat.expertise],
                  ["Experience", eeat.experience],
                  ["Authoritativeness", eeat.authoritativeness],
                  ["Trustworthiness", eeat.trustworthiness],
                ].map(([label, val]) => (
                  <div key={label} className="mb-3" data-testid={`eeat-${String(label).toLowerCase()}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
                      <span className="font-mono text-xs font-semibold">{val}/10 · {Math.round((val / 10) * 100)}%</span>
                    </div>
                    <div className="h-2 bg-secondary border border-border">
                      <div className="h-full bg-[#ffce00]" style={{ width: `${(val / 10) * 100}%` }} />
                    </div>
                  </div>
                ))}
                {eeat.notes && <p className="text-xs text-muted-foreground font-mono mt-3 leading-relaxed">{eeat.notes}</p>}
              </TabsContent>
            )}

            {result?.seo && (
              <TabsContent value="seo" className="p-5 m-0 space-y-3">
                <div className="flex justify-end">
                  <Button data-testid="download-seo" size="sm" variant="outline" className="rounded-none h-7"
                    onClick={() => download(`${result.seo.slug || "seo"}-seo.txt`, JSON.stringify(result.seo, null, 2))}>
                    <Download className="w-3 h-3 mr-1" aria-hidden="true" />Download SEO
                  </Button>
                </div>
                {SEO_FIELDS.map(([key, label]) => (
                  <div key={key} className="border border-border p-3" data-testid={`seo-${key}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
                      <button onClick={() => copy(String(result.seo[key] ?? ""))} className="text-muted-foreground hover:text-foreground"><Copy className="w-3 h-3" aria-hidden="true" /></button>
                    </div>
                    <p className="text-sm break-words font-mono">{String(result.seo[key] ?? "")}</p>
                  </div>
                ))}
                <div className="border border-border p-3" data-testid="seo-hashtags">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">5 Hashtags</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(result.seo.hashtags || []).map((h, i) => (
                      <span key={i} className="font-mono text-xs bg-secondary px-2 py-1">{h}</span>
                    ))}
                  </div>
                </div>
              </TabsContent>
            )}

            <TabsContent value="image" className="p-5 m-0">
              {image ? (
                <div>
                  <img src={image.data_url} alt="AI generated" className="w-full border border-border" />
                  <div className="flex items-center justify-between mt-3">
                    <span className="font-mono text-[11px] text-muted-foreground">{image.width}×{image.height} · {image.size_kb} KB</span>
                    <a data-testid="download-image" href={image.data_url} download={`${result?.seo?.slug || "td-news"}.jpg`}>
                      <Button size="sm" className="rounded-none h-7 bg-[#111110] hover:bg-[#ffce00] text-white hover:text-black transition-colors"><Download className="w-3 h-3 mr-1" aria-hidden="true" />Download JPG</Button>
                    </a>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <ImageIcon className="w-6 h-6 mx-auto mb-3 text-muted-foreground" aria-hidden="true" />
                  <p className="text-sm text-muted-foreground mb-3">Generate a minimalist 1200×675 image (&lt;77 KB).</p>
                  <Button data-testid="image-cta" onClick={genImage} disabled={imgLoading} variant="outline" className="rounded-none font-mono text-xs">
                    {imgLoading ? "Generating\u2026" : (premium ? "Generate image" : "Upgrade to generate")}
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
